import React from "react";
import { Dialog } from "../../../components/Dialogs/Dialog";
import { ISubscriptionPlan } from "../../../redux/subscription/subscription.types";
import { BillingMode } from "./changeBillingMode.types";
import { BILLING_MODE_LABELS } from "./changeBillingMode.constants";
import { useChangeBillingMode } from "./hooks/useChangeBillingMode";
import { SelectModeStep } from "./steps/SelectModeStep";
import { VerifyOtpStep } from "./steps/VerifyOtpStep";

interface IChangeBillingModeDialogProps {
  open: boolean;
  onClose: () => void;
  companyOutletUuid: string;
  currentBillingMode: BillingMode;
  userUuid: string;
  plan: ISubscriptionPlan;
  /** Called after a successful switch so the parent can refresh the plan list. */
  onSwitched: () => void;
}

export const ChangeBillingModeDialog: React.FC<IChangeBillingModeDialogProps> = ({
  open,
  onClose,
  companyOutletUuid,
  currentBillingMode,
  userUuid,
  plan,
  onSwitched,
}) => {
  const {
    step,
    targetMode,
    otp,
    otpError,
    resendTimer,
    loading,
    setOtp,
    sendOtp,
    verifyAndSwitch,
    resend,
  } = useChangeBillingMode({
    open,
    companyOutletUuid,
    currentBillingMode,
    userUuid,
    plan,
    onSwitched,
    onClose,
  });

  const actions =
    step === "MODE"
      ? [
          {
            label: "Cancel",
            type: "button" as const,
            variant: "text" as const,
            disabled: loading,
            onClick: onClose,
          },
          {
            label: loading ? "Sending…" : "Send OTP",
            type: "button" as const,
            variant: "contained" as const,
            disabled: loading,
            onClick: sendOtp,
          },
        ]
      : [
          {
            label: "Cancel",
            type: "button" as const,
            variant: "text" as const,
            disabled: loading,
            onClick: onClose,
          },
          {
            label: loading ? "Verifying…" : "Verify & Switch",
            type: "button" as const,
            variant: "contained" as const,
            disabled: loading || !otp,
            onClick: verifyAndSwitch,
          },
        ];

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="sm"
      title="Change billing mode"
      subtitle={
        step === "MODE"
          ? `Switch to ${BILLING_MODE_LABELS[targetMode]} billing`
          : "Verify to confirm the change"
      }
      actions={actions}
    >
      {step === "MODE" ? (
        <SelectModeStep
          currentMode={currentBillingMode}
          targetMode={targetMode}
        />
      ) : (
        <VerifyOtpStep
          otp={otp}
          otpError={otpError}
          resendTimer={resendTimer}
          loading={loading}
          onOtpChange={setOtp}
          onResend={resend}
        />
      )}
    </Dialog>
  );
};
